import type { FeatureToggles } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type { ShortCircuitScenarioResults } from '../../../..';
import {
  AttentionStatusStorage2,
  type ForStoringAttentionStatus2
} from '../../../../evaluation/AttentionStatusRepository';
import type { ForRuleProviding } from '../../../../evaluation/RulesProvider';
import { FaultCurrentEvaluationEngine } from '../../../../evaluation/shortCircuit/engines/mediumVoltage/faultCurrentEvaluationEngine';
import { ResultsFilter } from '../../../../filterOnlyMvBusbarResults';
import type { FunctionalGrid } from '../../../../grid/FunctionalGrid';
import { DetailsWithResultsFactory } from '../Details';
import { createExternalNetworkInjectionsShortCircuitDetails } from '../ExternalNetworkInjectionsDetails';
import { createPowerTransformersShortCircuitDetails } from '../PowerTransformersDetails';
import type { GridElementsShortCircuitDetails } from '../ShortCircuitDetails';
import { Features } from '../../../../createGridElementCalculationSetExecutionDetails';

export function createShortCircuitDetails(
  features: Features,
  shortCircuitScenarios: ShortCircuitScenarioResults[],
  functionalGrid: FunctionalGrid,
  rulesProvider: ForRuleProviding,
  attentionStatusStorage: ForStoringAttentionStatus2,
  featureToggles?: FeatureToggles
): GridElementsShortCircuitDetails {
  const mvEvaluationEngine = new FaultCurrentEvaluationEngine(
    attentionStatusStorage,
    rulesProvider
  );
  const lvEvaluationEngine = new FaultCurrentEvaluationEngine(
    AttentionStatusStorage2.noStorage(),
    rulesProvider
  );

  const mergedScenariosInputs = shortCircuitScenarios
    .flatMap(scenarioResults => scenarioResults.inputs)
    .filter(Boolean);

  const mergedScenariosResults = shortCircuitScenarios.flatMap(
    scenarioResults => scenarioResults.results
  );

  const filteredResults = ResultsFilter.Create(
    mergedScenariosResults,
    functionalGrid
  )
    .removeLvBusbarsFromResults()
    .removeExternalNetworkInjectionFromBusbarResults()
    .toResults();

  const details = DetailsWithResultsFactory.create(
    features,
    filteredResults,
    mvEvaluationEngine,
    featureToggles
  );

  const lvFilteredResults = ResultsFilter.Create(
    mergedScenariosResults,
    functionalGrid
  )
    .removeMvBusbarsFromResults()
    .toResults();

  const lvDetails = DetailsWithResultsFactory.create(
    features,
    lvFilteredResults,
    lvEvaluationEngine,
    featureToggles
  );

  const energyConsumerShortCircuitResults = details.for('EnergyConsumer');
  const mvBusbarShortCircuitResults = details.for('Busbar');
  const lvBusbarsShortCircuitResults = lvDetails.for('Busbar');
  const externalNetworkInjection =
    createExternalNetworkInjectionsShortCircuitDetails(
      features,
      mergedScenariosInputs
    );
  const powerTransformerShortCircuitResults =
    createPowerTransformersShortCircuitDetails(
      functionalGrid,
      features,
      mvEvaluationEngine,
      mergedScenariosResults,
      featureToggles
    );

  const allResults = [
    ...energyConsumerShortCircuitResults,
    ...mvBusbarShortCircuitResults,
    ...lvBusbarsShortCircuitResults,
    ...externalNetworkInjection,
    ...powerTransformerShortCircuitResults
  ];

  return allResults;
}
